import { Alert, Anchor, List, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { formatDate } from '@feature/utils';

import {
  TravelBlockingRule,
  TravelOverlapRequest,
  formatTravelRegistrationWithType,
} from '../model/TravelRequestTypes';

const blockingTexts = {
  erasmusCertificatePending: {
    title: 'notification.error.code.TRAVEL_ERASMUS_CERTIFICATE_PENDING',
    link: 'travel.requests.issues.blockingRequest',
  },
  earlierReturnPending: {
    title: 'notification.error.code.TRAVEL_EARLIER_RETURN_PENDING',
    link: 'travel.requests.issues.blockingReturnRequest',
  },
} as const satisfies Record<TravelBlockingRule, { title: string; link: string }>;

interface BlockingRequestLinkProps {
  request: TravelOverlapRequest;
  rule: TravelBlockingRule;
}

export function BlockingRequestLink({ request, rule }: BlockingRequestLinkProps) {
  const { t } = useTranslation();
  const { registrationNumber, registrationYear, registrationDate, registryType } = request;
  const registration =
    registrationNumber != null && registrationYear != null
      ? formatTravelRegistrationWithType({ registrationNumber, registrationYear, registrationDate, registryType }, t)
      : t('travel.overlaps.unregistered');

  return (
    <Anchor
      component={Link}
      to={`/travel/requests/${request.id}`}
      size="sm"
      fw="500"
    >
      {t(blockingTexts[rule].link, { registration })}
    </Anchor>
  );
}

export function useBlockingRequestDetail() {
  const { t } = useTranslation();

  return ({ destinationInstitution, departureDate, returnDate }: TravelOverlapRequest, rule: TravelBlockingRule) => {
    const dates = `${formatDate(departureDate) ?? '-'} - ${formatDate(returnDate) ?? '-'}`;
    return t(`travel.requests.issues.${rule}`, { destination: destinationInstitution ?? '-', dates });
  };
}

interface TravelBlockingRequestsAlertProps {
  requests: TravelOverlapRequest[];
  rule: TravelBlockingRule;
}

export function TravelBlockingRequestsAlert({ requests, rule }: TravelBlockingRequestsAlertProps) {
  const { t } = useTranslation();
  const detail = useBlockingRequestDetail();

  if (requests.length === 0) {
    return null;
  }

  return (
    <Alert
      color="red"
      icon={<IconAlertCircle size={18} />}
      title={t(blockingTexts[rule].title)}
    >
      <List size="sm">
        {requests.map(request => (
          <List.Item key={request.id}>
            <BlockingRequestLink
              request={request}
              rule={rule}
            />{' '}
            <Text
              span
              size="sm"
            >
              {detail(request, rule)}
            </Text>
          </List.Item>
        ))}
      </List>
    </Alert>
  );
}
